/**
 * Target Setup Action Definitions
 *
 * Actions for loading proteins, folding, alignment, and target designation.
 *
 * @module gibbs/registry/actions/target-setup
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── load-protein-from-pdb ──────────────────────────────────────────────────

export const loadProteinFromPdb = {
    id: 'load-protein-from-pdb',
    name: 'Load Protein from PDB ID',
    category: 'Target Setup',
    description: 'Fetch a protein structure from the RCSB Protein Data Bank by its 4-character PDB code and load it into the workspace. Initializes a CaseData with protein chains, residues, atoms, sample compounds, and fragment map metadata.',
    inputs: [
        param('pdbId', 'string', '4-character PDB identifier'),
    ],
    outputs: [
        param('caseData', T.CaseData, 'Loaded protein case with chains, residues, atoms'),
        param('fragmentMap', T.FragmentMap, 'Associated fragment availability metadata'),
        param('sampleCompounds', T.CompoundList, 'Co-crystallized ligands if available', false),
    ],
    parameters: [
        param('keepExisting', 'boolean', 'Keep currently loaded proteins (enables multi-protein)', false, { defaultValue: false }),
        param('fragmentLoading', 'string', 'Fragment loading strategy', false),
        param('preserveHydrogens', 'boolean', 'Retain hydrogen atoms from PDB', false, { defaultValue: false }),
        param('forcefield', 'string', 'Force field to apply (e.g., Amber)', false),
        param('biologicalAssembly', 'boolean', 'Load biological assembly vs. asymmetric unit', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [
        post('protein_loaded', 'A protein is loaded in the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ChooseProtein',
    serverCommand: 'load-pdb-id',
    jobType: null,
};

// ─── load-protein-from-file ─────────────────────────────────────────────────

export const loadProteinFromFile = {
    id: 'load-protein-from-file',
    name: 'Load Protein from File',
    category: 'Target Setup',
    description: 'Load a protein structure from a user-uploaded PDB or mmCIF file string. Supports structures from folding predictions, external tools, or local files.',
    inputs: [
        param('data', 'string', 'PDB/CIF format text'),
        param('moleculeName', 'string', 'Display name for the protein'),
        param('fileType', 'string', 'Format type: pdb or mmcif'),
    ],
    outputs: [
        param('caseData', T.CaseData, 'Loaded protein case'),
    ],
    parameters: [
        param('keepExisting', 'boolean', 'Keep currently loaded proteins', false, { defaultValue: false }),
        param('fragmentLoading', 'string', 'Fragment loading strategy', false),
        param('preserveHydrogens', 'boolean', 'Retain hydrogen atoms', false, { defaultValue: false }),
        param('forcefield', 'string', 'Force field to apply', false),
        param('biologicalAssembly', 'boolean', 'Load biological assembly', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [
        post('protein_loaded', 'A protein is loaded in the workspace'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ChooseProtein',
    serverCommand: 'load-pdb-string',
    jobType: null,
};

// ─── load-protein-from-map ──────────────────────────────────────────────────

export const loadProteinFromMap = {
    id: 'load-protein-from-map',
    name: 'Load Protein from BMaps Map',
    category: 'Target Setup',
    description: 'Load a precomputed BMaps protein case (with pre-simulated fragment maps) from the BMaps data server. These are the ~600 therapeutically important proteins with pre-computed Boltzmann Maps.',
    inputs: [
        param('caseInfo', T.CaseData, 'Map case identifier from the map selector'),
    ],
    outputs: [
        param('caseData', T.CaseData, 'Full case with protein, fragments, energies, solvation data'),
        param('fragmentMap', T.FragmentMap, 'Pre-simulated fragment binding data'),
    ],
    parameters: [
        param('keepExisting', 'boolean', 'Keep currently loaded proteins (multi-protein mode)', false, { defaultValue: false }),
        param('fragmentLoading', 'string', 'How to load pre-computed fragments', false),
    ],
    dependsOn: [],
    preconditions: [],
    postconditions: [
        post('protein_loaded', 'A protein is loaded in the workspace'),
        post('fragment_map_available', 'Fragment map is available for the loaded protein'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ChooseProtein',
    serverCommand: 'get-case-files',
    jobType: null,
};

// ─── fold-protein ───────────────────────────────────────────────────────────

export const foldProtein = {
    id: 'fold-protein',
    name: 'Predict Protein Structure (AI Folding)',
    category: 'Target Setup',
    description: 'Submit a protein sequence to an AI structure prediction service (Boltz-1, Boltz-2, or Chai-1) and load the resulting 3D structure into the workspace. Long-running GPU computation.',
    inputs: [
        param('foldingInput', T.FoldingInput, 'Sequences, ligands, folding program ID, and folding args'),
    ],
    outputs: [
        param('proteinStructure', T.ProteinStructure, 'Predicted 3D structure'),
        param('caseData', T.CaseData, 'Loaded into workspace'),
        param('resultProperties', T.PropertySet, 'Confidence metrics (pLDDT, pTM, etc.)'),
    ],
    parameters: [
        param('foldingProgramId', 'string', 'Folding program to use', false, { options: ['boltz-1', 'boltz-2', 'chai-1'] }),
        param('foldingArgs', 'object', 'Algorithm-specific parameters', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('jobman_available', 'Jobman service must be available for GPU computation'),
    ],
    postconditions: [
        post('protein_loaded', 'A protein is loaded in the workspace'),
    ],
    runtime: 'background',
    status: 'implemented',
    userAction: 'FoldAndLoad',
    serverCommand: 'fold-protein',
    jobType: 'folding',
};

// ─── align-protein ──────────────────────────────────────────────────────────

export const alignProtein = {
    id: 'align-protein',
    name: 'Align Protein Structures',
    category: 'Target Setup',
    description: 'Compute and apply a structural alignment between two loaded proteins. Used for selectivity studies and multi-target comparison.',
    inputs: [
        param('refCaseData', T.CaseData, 'Reference protein (stays fixed)'),
        param('alignCaseData', T.CaseData, 'Protein to align (will be transformed)'),
        param('refSpec', 'string', 'Chain/residue spec for reference', false),
        param('alignSpec', 'string', 'Chain/residue spec for alignment target', false),
    ],
    outputs: [
        param('transform', 'object', 'Applied rotation/translation transform stored on alignCaseData'),
    ],
    parameters: [],
    dependsOn: ['load-protein-from-pdb'],
    preconditions: [
        pre('multiple_proteins_loaded', 'At least two proteins must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'AlignProtein',
    serverCommand: 'align-protein',
    jobType: null,
};

// ─── set-protein-target ─────────────────────────────────────────────────────

export const setProteinTarget = {
    id: 'set-protein-target',
    name: 'Designate Protein as Target',
    category: 'Target Setup',
    description: 'Mark a loaded protein as a selectivity target. Used in multi-protein scoring workflows to distinguish primary target from off-targets.',
    inputs: [
        param('mapCase', T.CaseData, 'Protein to designate'),
        param('targetInfo', 'object', '{isTarget: boolean}'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'SetProteinTarget',
    serverCommand: null,
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    loadProteinFromPdb,
    loadProteinFromFile,
    loadProteinFromMap,
    foldProtein,
    alignProtein,
    setProteinTarget,
];
